import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { CheckCircle, XCircle, Crown, Building2, UserCheck, Handshake, ShoppingCart } from "lucide-react";

// Importar el servicio que acabamos de crear
import rolePermissionService from "services/rolePermissionService";

function GestionPermisos() {
  // Estados para datos del backend
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para la UI
  const [hasChanges, setHasChanges] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [saving, setSaving] = useState(false);

  // Mapeo de iconos para los roles (esto lo mantenemos estático por ahora)
  const roleIcons = {
    1: { icon: Crown, color: "primary", badge: "Admin" },
    2: { icon: Building2, color: "info", badge: "Org" },
    3: { icon: UserCheck, color: "success", badge: "Evaluador" },
    4: { icon: Handshake, color: "warning", badge: "Partner" },
    5: { icon: ShoppingCart, color: "secondary", badge: "Ventas" },
  };

  // Función para cargar datos del backend
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Cargando datos de roles y permisos...');
      
      // Obtener la matriz completa de roles y permisos
      const matrix = await rolePermissionService.getRolePermissionMatrix();
      
      console.log('📊 Datos recibidos:', matrix);
      
      // Establecer roles
      setRoles(matrix.roles.map(role => ({
        ...role,
        // Mapear los campos del backend a los esperados por el frontend
        id: role.rol_id || role.id,
        name: role.nombre || role.name,
        // Agregar configuraciones de UI
        ...roleIcons[role.rol_id || role.id] || { 
          icon: UserCheck, 
          color: "secondary", 
          badge: "Role" 
        }
      })));
      
      // Establecer permisos
      setPermissions(matrix.permissions.map(permission => ({
        ...permission,
        // Mapear los campos del backend a los esperados por el frontend
        id: permission.id_permiso || permission.id,
        name: permission.nombre || permission.name,
        description: permission.descripcion || permission.description || 'Sin descripción'
      })));
      
      // Establecer relaciones de permisos por rol
      const permissionMap = {};
      Object.keys(matrix.relations).forEach(roleId => {
        permissionMap[roleId] = matrix.relations[roleId] || [];
      });
      
      setRolePermissions(permissionMap);
      
      console.log('✅ Datos cargados exitosamente');
      console.log('Roles:', matrix.roles.length);
      console.log('Permisos:', matrix.permissions.length);
      console.log('Relaciones:', Object.keys(permissionMap).length);
      
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      setError('Error al cargar los datos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    loadData();
  }, []);

  const togglePermission = (roleId, permissionId) => {
    setRolePermissions((prev) => {
      const current = prev[roleId] || [];
      const has = current.includes(permissionId);
      return {
        ...prev,
        [roleId]: has ? current.filter((p) => p !== permissionId) : [...current, permissionId],
      };
    });
    setHasChanges(true);
  };

  const hasPermission = (roleId, permissionId) => 
    rolePermissions[roleId]?.includes(permissionId) || false;

  const getActivePermissions = (roleId) => 
    rolePermissions[roleId]?.length || 0;

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Guardando cambios:', rolePermissions);

      // Actualizar permisos para cada rol que haya cambiado
      const savePromises = roles.map(async (role) => {
        const newPermissions = rolePermissions[role.id] || [];
        return await rolePermissionService.updateRolePermissions(role.id, newPermissions);
      });

      await Promise.all(savePromises);

      setShowNotification(true);
      setHasChanges(false);
      
      console.log('✅ Cambios guardados exitosamente');
      
      // Ocultar notificación después de 3 segundos
      setTimeout(() => setShowNotification(false), 3000);
      
    } catch (error) {
      console.error('❌ Error guardando cambios:', error);
      setError('Error al guardar los cambios. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    console.log('🔄 Recargando datos originales...');
    loadData(); // Recargar datos originales
    setHasChanges(false);
  };

  // Mostrar loading
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Card>
                <MDBox p={6} textAlign="center">
                  <CircularProgress color="info" size={40} />
                  <MDTypography variant="h6" mt={2}>
                    Cargando roles y permisos...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <XCircle size={48} color="#f44336" />
                  <MDTypography variant="h6" mt={2} color="error">
                    {error}
                  </MDTypography>
                  <MDButton 
                    variant="gradient" 
                    color="info" 
                    onClick={loadData}
                    sx={{ mt: 2 }}
                  >
                    <Icon sx={{ mr: 1 }}>refresh</Icon>
                    Reintentar
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const RoleCell = ({ role }) => {
    const IconComponent = role.icon;
    
    return (
      <MDBox display="flex" alignItems="center" gap={1.5}>
        <Tooltip title={role.name} arrow placement="top">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #6a0dad 0%, #4b0082 100%)",
              color: "#fff",
              boxShadow: "0 2px 8px rgba(106, 13, 173, 0.2)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(106, 13, 173, 0.3)"
              }
            }}
          >
            <IconComponent size={20} strokeWidth={2.5} />
          </Box>
        </Tooltip>
        <MDBox>
          <MDTypography variant="button" fontWeight="medium" sx={{ lineHeight: 1.3 }}>
            {role.name}
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={0.5} mt={0.25}>
            <MDBadge 
              badgeContent={`${getActivePermissions(role.id)}/${permissions.length}`} 
              color={role.color} 
              size="xs" 
              variant="contained"
            />
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  RoleCell.propTypes = {
    role: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      color: PropTypes.string.isRequired,
    }).isRequired,
  };

  const PermissionSwitch = ({ roleId, permissionId, permissionName }) => (
    <Tooltip 
      title={`${permissionName}: ${hasPermission(roleId, permissionId) ? "Activo" : "Inactivo"} (Solo lectura)`}
      arrow
    >
      <Box sx={{ display: "inline-flex" }}>
        <Switch
          size="small"
          checked={hasPermission(roleId, permissionId)}
          disabled={true} // 🔒 VISTA SOLO LECTURA - Para reactivar: cambiar a false y descomentar onChange
          // onChange={() => togglePermission(roleId, permissionId)} // 🔒 COMENTADO: funcionalidad de edición
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#4ade80 !important"
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#14b34eff !important"
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#d1d5db !important"
            },
            // 🎨 Estilos especiales para switches deshabilitados pero VISIBLES
            "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
              color: "#22c55e !important", // Verde más fuerte para permisos activos
              opacity: "1 !important", // Sin transparencia para permisos activos
            },
            "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
              backgroundColor: "#16a34a !important", // Verde del track para activos
              opacity: "1 !important", // Sin transparencia para el track activo
            },
            "& .MuiSwitch-switchBase.Mui-disabled": {
              color: "#9ca3af !important", // Gris para permisos inactivos
              opacity: "0.6 !important", // Transparencia para inactivos
            },
            "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
              backgroundColor: "#e5e7eb !important", // Gris claro para track inactivo
              opacity: "0.6 !important", // Transparencia para track inactivo
            }
          }}
          inputProps={{
            "aria-label": `${roles.find(r => r.id === roleId)?.name} - ${permissionName}`,
            role: "switch"
          }}
        />
      </Box>
    </Tooltip>
  );

  PermissionSwitch.propTypes = {
    roleId: PropTypes.number.isRequired,
    permissionId: PropTypes.number.isRequired,
    permissionName: PropTypes.string.isRequired,
  };

  const columns = [
    {
      Header: "Rol",
      accessor: "role",
      width: "25%",
      align: "left",
    },
    ...permissions.map((permission) => ({
      Header: (
        <Tooltip title={permission.description} arrow>
          <span style={{ cursor: "help" }}>{permission.name}</span>
        </Tooltip>
      ),
      accessor: `permission_${permission.id}`,
      align: "center",
      width: `${75 / permissions.length}%`,
    })),
  ];

  const rows = roles.map((role) => {
    const row = {
      role: <RoleCell role={role} />,
    };
    
    permissions.forEach((permission) => {
      row[`permission_${permission.id}`] = (
        <PermissionSwitch
          roleId={role.id}
          permissionId={permission.id}
          permissionName={permission.name}
        />
      );
    });
    
    return row;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6" color="white">
                      Roles & Permisos
                    </MDTypography>
                    
                  </MDBox>
                  {/* 🔒 BOTÓN COMENTADO: Para reactivar funcionalidad de edición */}
                  {/* 
                  <MDButton
                    variant="contained"
                    color="white"
                    size="small"
                    sx={{
                      backgroundColor: "#fff",
                      color: "#0c0e10ff",
                      "&:hover": {
                        backgroundColor: "#f5f5f5"
                      }
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: 18 }}>add</Icon>
                    Nuevo Rol
                  </MDButton>
                  */}
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDBox display="flex" gap={3} alignItems="center">
                    <MDBox display="flex" gap={1} alignItems="center">
                      <Box sx={{ width: 44, height: 22, borderRadius: 2, background: "linear-gradient(90deg,#22c55e,#16a34a)" }} />
                      <MDTypography variant="body2">Permiso Activo</MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={1} alignItems="center">
                      <Box sx={{ width: 44, height: 22, borderRadius: 2, background: "#e5e7eb" }} />
                      <MDTypography variant="body2">Permiso Inactivo</MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* 🔒 INDICADOR DE ESTADO COMENTADO: Ya no es necesario mostrar cambios */}
                  {/*
                  <MDTypography variant="caption" color="text">
                    {hasChanges ? (
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <XCircle style={{ color: "#f97316", width: 16, height: 16 }} />
                        Hay cambios sin guardar
                      </MDBox>
                    ) : (
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <CheckCircle style={{ color: "#16a34a", width: 16, height: 16 }} />
                        Todo guardado
                      </MDBox>
                    )}
                  </MDTypography>
                  */}
                  
                  {/* 📖 NUEVO: Indicador de solo lectura */}
                  <MDTypography variant="caption" color="text">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon style={{ color: "#4ade80", fontSize: 16 }}>visibility</Icon>
                      Solo consulta
                    </MDBox>
                  </MDTypography>
                </MDBox>

                {showNotification && (
                  <MDBox 
                    display="flex" 
                    alignItems="center" 
                    gap={2} 
                    sx={{ 
                      mt: 2, 
                      p: 2, 
                      backgroundColor: "#f0fdf4", 
                      borderRadius: 1,
                      border: "1px solid #86efac"
                    }}
                  >
                    <CheckCircle style={{ color: "#22c55e" }} />
                    <MDTypography variant="body2" color="success">
                      ¡Cambios guardados! Los permisos se actualizaron correctamente
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>

              <MDBox pt={2} pb={3} px={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
              </MDBox>

              {/* 🔒 BOTONES DE ACCIÓN COMENTADOS: Para reactivar funcionalidad de edición */}
              {/*
              <MDBox p={3} display="flex" justifyContent="flex-end" gap={1} borderTop="1px solid #f0f0f0">
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleCancel}
                  disabled={!hasChanges || saving}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
                      Guardar Cambios
                    </>
                  )}
                </MDButton>
              </MDBox>
              */}
              
              {/* 📊 INFORMACIÓN SOLO LECTURA */}
              <MDBox p={3} borderTop="1px solid #f0f0f0">
                <MDTypography variant="caption" color="text" textAlign="center">
                  Vista de solo consulta - Los permisos se muestran como están configurados actualmente
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default GestionPermisos;
